import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export const keys = {
  fleet: ["fleet"],
  config: ["config"],
  bookings: (filter) => ["bookings", filter],
  booking: (id) => ["booking", id],
  chat: ["chat"],
  geo: (q) => ["geo", q],
  ownerSummary: ["owner", "summary"],
  ownerBookings: (filter, payment) => ["owner", "bookings", filter, payment],
  ownerClients: ["owner", "clients"],
};

const get = (url, params) => api.get(url, { params }).then((r) => r.data);

export const useFleet = () => useQuery({ queryKey: keys.fleet, queryFn: () => get("/fleet"), staleTime: Infinity });
export const useAppConfig = () =>
  useQuery({ queryKey: keys.config, queryFn: () => get("/config"), staleTime: Infinity });
export const useBookings = (filter) =>
  useQuery({ queryKey: keys.bookings(filter), queryFn: () => get("/bookings", { filter }) });
export const useBooking = (id) => useQuery({ queryKey: keys.booking(id), queryFn: () => get(`/bookings/${id}`) });
export const useChatMessages = () => useQuery({ queryKey: keys.chat, queryFn: () => get("/concierge/messages") });
export const useGeoSearch = (q) =>
  useQuery({ queryKey: keys.geo(q), queryFn: () => get("/geo/search", { q }), enabled: q.length >= 3, staleTime: Infinity, retry: 0 });
export const useQuote = (params) =>
  useQuery({
    queryKey: ["quote", params],
    queryFn: () => api.post("/quote", params).then((r) => r.data),
    enabled: Boolean(params),
    placeholderData: keepPreviousData,
    retry: 0,
  });

function useBookingMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => {
      const booking = data.booking || data;
      qc.setQueryData(keys.booking(booking.id), booking);
      qc.invalidateQueries({ queryKey: ["bookings"] });
    },
  });
}

export const useCreateBooking = () => useBookingMutation((body) => api.post("/bookings", body).then((r) => r.data));
export const useCancelBooking = () =>
  useBookingMutation((id) => api.post(`/bookings/${id}/cancel`).then((r) => r.data));
export const useStartPayment = () =>
  useBookingMutation(({ id, method }) => api.post(`/bookings/${id}/payment`, { method }).then((r) => r.data));
export const useConfirmPayment = () =>
  useBookingMutation(({ id, method }) => api.post(`/bookings/${id}/payment/confirm`, { method }).then((r) => r.data));

export const reverseGeocode = (lat, lng) =>
  get("/geo/reverse", { lat, lng }).catch(() => ({ address: `${lat.toFixed(5)}, ${lng.toFixed(5)}`, lat, lng }));

export const useOwnerSummary = () => useQuery({ queryKey: keys.ownerSummary, queryFn: () => get("/owner/summary") });
export const useOwnerBookings = (filter, payment) =>
  useQuery({ queryKey: keys.ownerBookings(filter, payment), queryFn: () => get("/owner/bookings", { filter, payment }) });
export const useOwnerClients = () => useQuery({ queryKey: keys.ownerClients, queryFn: () => get("/owner/clients") });

export function useSendReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/owner/bookings/${id}/remind`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["owner", "bookings"] }),
  });
}

export function useReviewPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision }) => api.post(`/owner/bookings/${id}/payment-review`, { decision }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["owner"] }),
  });
}

export function useSetQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fare }) => api.post(`/owner/bookings/${id}/quote`, { fare }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["owner"] }),
  });
}

export const updatePreferences = (prefs) => api.patch("/me/preferences", prefs).then((r) => r.data);
